import { intervalToDuration } from 'date-fns'

import { Duration } from '@blade/std-lib'
import { DurationFormatter } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'

import type { ServicesApi } from '@root/shared/api'
import type { Locale } from 'date-fns'

export type SettingVariant = 'low' | 'market' | 'high'

export interface Info {
  variants: Record<SettingVariant, SuggestedSettings>
  estimatedBaseFeeGwei: number
  networkCongestion: number
  latestPriorityFeeRange: [number, number]
  historicalPriorityFeeRange: [number, number]
  historicalBaseFeeRange: [number, number]
  priorityFeeTrend: Trend.Trend
  baseFeeTrend: Trend.Trend
}

export type SuggestedSettings = {
  /**
   * The maximum suggested priority fee per gas (in gwei) to pay to have transactions included in a block.
   * */
  suggestedMaxPriorityFeePerGas: number
  /**
   * The maximum suggested total fee per gas to pay (in gwei), including both the base fee and the priority fee.
   */
  suggestedMaxFeePerGas: number
  /**
   * The minimum estimated wait time (in milliseconds) for a transaction to be included in a block at the suggested gas price.
   */
  minWaitTimeEstimate: Duration.Duration
  /**
   * The maximum estimated wait time (in milliseconds) for a transaction to be included in a block at the suggested gas price.
   */
  maxWaitTimeEstimate: Duration.Duration
  averageWaitTimeEstimate: Duration.Duration
  variant: SettingVariant
}

export const averageWaitTimeEstimate = (entry: SuggestedSettings) => {
  return Duration.sum(
    entry.maxWaitTimeEstimate,
    entry.minWaitTimeEstimate,
  ).pipe(Duration.divideUnsafe(2))
}

const TRANSFER_GAS = 21_000

export const calculateTransferMaxFeeInGwei = (option: SuggestedSettings) => {
  const totalForTransaction =
    Number(option.suggestedMaxFeePerGas) * TRANSFER_GAS
  return totalForTransaction
}

export const calculateTransferEstimatedFeeInGwei = (
  option: SuggestedSettings,
  estimatedBaseFee: number,
) => {
  const feePerGas =
    estimatedBaseFee + Number(option.suggestedMaxPriorityFeePerGas)

  const totalForTransaction = feePerGas * TRANSFER_GAS
  return totalForTransaction
}

const trendFromDto = (dto: string): Trend.Trend => {
  if (dto === 'up') {
    return Trend.Up
  }
  return Trend.Down
}

const mapEntry = (
  dto: ServicesApi.InfuraSuggestedGasSettingsEntryDto,
  variant: SettingVariant,
): SuggestedSettings => {
  const maxWaitTimeEstimate = Duration.millis(dto.maxWaitTimeEstimate)
  const minWaitTimeEstimate = Duration.millis(dto.minWaitTimeEstimate)
  const averageWaitTimeEstimate = Duration.sum(
    maxWaitTimeEstimate,
    minWaitTimeEstimate,
  ).pipe(Duration.divideUnsafe(2))

  return {
    averageWaitTimeEstimate,
    maxWaitTimeEstimate,
    minWaitTimeEstimate,
    variant: variant,
    suggestedMaxFeePerGas: Number(dto.suggestedMaxFeePerGas),
    suggestedMaxPriorityFeePerGas: Number(dto.suggestedMaxPriorityFeePerGas),
  }
}

export const fromDto = (
  dto: ServicesApi.InfuraSuggestedGasSettingsDto,
): Info => {
  return {
    baseFeeTrend: trendFromDto(dto.baseFeeTrend),
    variants: {
      high: mapEntry(dto.high, 'high'),
      market: mapEntry(dto.medium, 'market'),
      low: mapEntry(dto.low, 'low'),
    },
    estimatedBaseFeeGwei: Number(dto.estimatedBaseFee),
    historicalBaseFeeRange: [
      Number(dto.historicalBaseFeeRange[0]),
      Number(dto.historicalBaseFeeRange[1]),
    ],
    historicalPriorityFeeRange: [
      Number(dto.historicalPriorityFeeRange[0]),
      Number(dto.historicalPriorityFeeRange[1]),
    ],
    latestPriorityFeeRange: [
      Number(dto.latestPriorityFeeRange[0]),
      Number(dto.latestPriorityFeeRange[1]),
    ],
    networkCongestion: dto.networkCongestion,
    priorityFeeTrend: trendFromDto(dto.priorityFeeTrend),
  }
}

/**
 * Formats the wait time in a human readable format: X minutes or X seconds.
 * @param waitTimeMs - The wait time in milliseconds.
 * @param locale - The locale to use for formatting.
 * @returns The formatted wait time or undefined if the wait time is less than 1 second.
 */
export const formatWaitTime = (
  waitTime: Duration.Duration,
  locale: Locale,
): string | undefined => {
  const formatted = DurationFormatter.formatDurationSingleUnit(
    intervalToDuration({ start: 0, end: Duration.toMillis(waitTime) }),
    { locale },
  )

  return formatted
}
